const CHROME_STORE_URL =
  'https://chromewebstore.google.com/detail/praxis-autoapply/nnbkmohccbjjbgfnjlmehaiddeiafjnk'

export default function AutoApply() {
  return (
    <div>
      <section className="py-16 md:py-24 px-6" aria-labelledby="autoapply-heading">
        <div className="max-w-3xl mx-auto text-center">
          <h1
            id="autoapply-heading"
            className="text-3xl md:text-4xl font-semibold text-text-primary mb-6 leading-tight"
          >
            Stop retyping your CV into every application form.
          </h1>
          <p className="text-lg text-text-secondary mb-8 max-w-2xl mx-auto">
            Upload your CV once. Praxis AutoApply extracts your details and fills job
            application forms for you. One click on any application page. Your data stays
            in your browser. No accounts. No servers.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href={CHROME_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-primary"
            >
              Install from Chrome Web Store
            </a>
            <a href="#how-it-works" className="btn btn-secondary">
              See How It Works
            </a>
          </div>
        </div>
      </section>

      <section className="py-16 px-6 bg-[#F5F5F3]" aria-labelledby="value-heading">
        <div className="max-w-5xl mx-auto">
          <h2 id="value-heading" className="sr-only">
            Why Praxis AutoApply
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <article className="card">
              <h3 className="font-semibold text-lg text-text-primary mb-3">
                One click. Done.
              </h3>
              <p className="text-sm text-text-secondary">
                Upload your CV once. Praxis extracts your details. After that, just click
                &quot;Fill this page&quot; on any application form.
              </p>
            </article>

            <article className="card">
              <h3 className="font-semibold text-lg text-text-primary mb-3">
                Your data stays put.
              </h3>
              <p className="text-sm text-text-secondary">
                No servers. No accounts. No tracking. Everything is stored locally in your
                browser. We never see your information.
              </p>
            </article>

            <article className="card">
              <h3 className="font-semibold text-lg text-text-primary mb-3">
                Works where you apply.
              </h3>
              <p className="text-sm text-text-secondary">
                Workday, Greenhouse, Lever, and most other application platforms. If it has
                form fields, Praxis can probably fill them.
              </p>
            </article>
          </div>
        </div>
      </section>

      <section
        id="how-it-works"
        className="py-16 px-6"
        aria-labelledby="how-heading"
      >
        <div className="max-w-3xl mx-auto">
          <h2 id="how-heading" className="text-2xl font-semibold text-text-primary mb-8">
            How it works
          </h2>
          <ol className="space-y-4">
            <li className="card flex gap-4">
              <span
                className="shrink-0 w-8 h-8 rounded-full bg-action-primary text-white font-medium flex items-center justify-center text-sm"
                aria-hidden
              >
                1
              </span>
              <div>
                <h3 className="font-medium text-text-primary mb-1">
                  Install the extension from the Chrome Web Store
                </h3>
                <p className="text-sm text-text-secondary">
                  One click. No account required.
                </p>
              </div>
            </li>
            <li className="card flex gap-4">
              <span
                className="shrink-0 w-8 h-8 rounded-full bg-action-primary text-white font-medium flex items-center justify-center text-sm"
                aria-hidden
              >
                2
              </span>
              <div>
                <h3 className="font-medium text-text-primary mb-1">
                  Upload your CV or enter your details manually
                </h3>
                <p className="text-sm text-text-secondary">
                  PDF or text. Praxis extracts name, work history, skills, education.
                </p>
              </div>
            </li>
            <li className="card flex gap-4">
              <span
                className="shrink-0 w-8 h-8 rounded-full bg-action-primary text-white font-medium flex items-center justify-center text-sm"
                aria-hidden
              >
                3
              </span>
              <div>
                <h3 className="font-medium text-text-primary mb-1">
                  Go to any job application form
                </h3>
                <p className="text-sm text-text-secondary">
                  Navigate to the application page as you normally would.
                </p>
              </div>
            </li>
            <li className="card flex gap-4">
              <span
                className="shrink-0 w-8 h-8 rounded-full bg-action-primary text-white font-medium flex items-center justify-center text-sm"
                aria-hidden
              >
                4
              </span>
              <div>
                <h3 className="font-medium text-text-primary mb-1">
                  Click &quot;Fill this page&quot; in the extension popup
                </h3>
                <p className="text-sm text-text-secondary">
                  Fields fill automatically. Review and submit.
                </p>
              </div>
            </li>
            <li className="card flex gap-4">
              <span
                className="shrink-0 w-8 h-8 rounded-full bg-action-primary text-white font-medium flex items-center justify-center text-sm"
                aria-hidden
              >
                5
              </span>
              <div>
                <h3 className="font-medium text-text-primary mb-1">
                  Review the filled fields and submit your application
                </h3>
                <p className="text-sm text-text-secondary">
                  Check everything looks right. Hit submit.
                </p>
              </div>
            </li>
          </ol>
        </div>
      </section>

      <section
        className="py-16 px-6 bg-[#F5F5F3]"
        aria-labelledby="privacy-heading"
      >
        <div className="max-w-3xl mx-auto">
          <h2 id="privacy-heading" className="text-2xl font-semibold text-text-primary mb-6">
            We can&apos;t see your data. By design.
          </h2>
          <p className="text-text-secondary mb-4">
            Your CV and application data are stored only in your browser&apos;s local
            storage. Nothing is sent to our servers. There are no servers for this. No
            analytics. No telemetry. No server-side processing.
          </p>
          <p className="text-text-secondary">
            The extension requests only the minimum permissions needed to fill form fields
            on the tab you have open. It doesn&apos;t read your browsing history or track
            which jobs you apply to. Your data stays yours.
          </p>
        </div>
      </section>

      <section className="py-16 px-6" aria-labelledby="cta-heading">
        <div className="max-w-2xl mx-auto text-center">
          <h2 id="cta-heading" className="text-2xl font-semibold text-text-primary mb-4">
            Filling in the same form 50 times isn&apos;t a character-building exercise.
          </h2>
          <p className="text-text-secondary mb-6">
            Free. Private. One click.
          </p>
          <a
            href={CHROME_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-primary"
          >
            Add to Chrome
          </a>
        </div>
      </section>
    </div>
  )
}
